import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import React from 'react';
import { Form } from 'react-bootstrap';
import { PlusIcon, MinusIcon } from '../../shared/icons/Svgs';
import type { ColumnDef } from '@tanstack/react-table';
import type { IIncomeQuantityType } from '../../../interfaces/IIncomeQuantityType';
import type { IIncomeCategory } from '../../../interfaces/IIncomeCategory';
import type { ILocation } from '../../../interfaces/ILocation';
import type { IIncome } from '../../../interfaces/IIncome';
import LocationsAutosuggest from '../../shared/locations-autosuggest/LocationsAutosuggest';

export interface IPerformncomeCalendarsSynchronizationIncomesTableProps {
  items: IIncome[];
  setItems: React.Dispatch<React.SetStateAction<IIncome[]>>;
  quantityTypes: IIncomeQuantityType[];
  categories: IIncomeCategory[];
  locations: ILocation[];
  updateIncomeCalendarSynchronization: (rowIndex: number, columnId: string, value: any) => void;
}

function PerformIncomeCalendarSynchronizationIable(props: IPerformncomeCalendarsSynchronizationIncomesTableProps) {
  const quantityTypeItems: any[] = [];
  const categories: any[] = [];
  for (let ind = 0; ind !== (props.quantityTypes?.length ?? 0); ind++) {
    quantityTypeItems.push(
      <option key={props.quantityTypes[ind]?.incomeQuantityTypeId} value={JSON.stringify(props.quantityTypes[ind])}>
        {props.quantityTypes[ind]?.incomeQuantityTypeName ?? ''}
      </option>
    );
  }
  for (let ind = 0; ind !== (props.categories?.length ?? 0); ind++) {
    categories.push(
      <option key={props.categories[ind]?.incomeCategoryId} value={JSON.stringify(props.categories[ind])}>
        {props.categories[ind]?.incomeCategoryName ?? ''}
      </option>
    );
  }

  const { setItems } = props;

  const columns = React.useMemo<ColumnDef<IIncome>[]>(
    () => [
      {
        id: 'actionButton',
        header: () => (
          <div
            title='Dodaj element'
            onClick={() => {
              setItems([
                ...props.items,
                {
                  incomeId: 0,
                  description: '',
                  quantity: 0,
                  revenue: 0,
                  location: undefined,
                  category:
                    props.categories.length > 0
                      ? props.categories[0]
                      : {
                          incomeCategoryId: undefined,
                          incomeCategoryName: '',
                        },
                  quantityType: {
                    incomeQuantityTypeId: 1,
                    incomeQuantityTypeShortName: 'szt.',
                    incomeQuantityTypeName: 'Sztuk',
                  },
                },
              ]);
            }}
          >
            <PlusIcon style={{ cursor: 'pointer', width: '30px', height: '30px', fill: 'var(--app-main-color)' }} />
          </div>
        ),
        cell: (info) => (
          <div
            title='Usuń element'
            onClick={() => {
              const selectedItems = [info.row.original];
              const updatedElements = [...props.items].filter((item) => !selectedItems.includes(item));
              setItems(updatedElements);
            }}
          >
            <MinusIcon style={{ cursor: 'pointer', width: '30px', height: '30px', fill: '#dc3545' }} />
          </div>
        ),
      },
      {
        header: () => <span style={{ userSelect: 'none' }}>Opis</span>,
        accessorFn: (row) => row?.description ?? '',
        id: 'description',
      },
      {
        header: () => <span style={{ userSelect: 'none' }}>Lokalizacja</span>,
        accessorFn: (row) => row?.location,
        id: 'location',
      },
      {
        header: () => <span style={{ userSelect: 'none' }}>Kategoria</span>,
        accessorFn: (row) => row?.category,
        id: 'category',
      },
      {
        header: () => <span style={{ userSelect: 'none' }}>Kwota</span>,
        accessorFn: (row) => row?.revenue ?? '',
        id: 'revenue',
      },
      {
        header: () => <span style={{ userSelect: 'none' }}>Ilość</span>,
        accessorFn: (row) => row?.quantity ?? '',
        id: 'quantity',
      },
      {
        header: () => <span style={{ userSelect: 'none' }}>Jednostka</span>,
        accessorFn: (row) => row?.quantityType,
        id: 'quantityType',
      },
    ],
    [setItems, props.items, props.categories]
  );

  const tbodyRef = React.useRef(null);

  const handleKeyDown = (event: any, row: any) => {
    event.stopPropagation();
    if (tbodyRef.current !== null) {
      const currentRow = (tbodyRef.current as any).children.namedItem(row.id);
      switch (event.key) {
        case 'ArrowUp':
          currentRow?.previousElementSibling?.focus();
          break;
        case 'ArrowDown':
          currentRow?.nextElementSibling?.focus();
          break;
        default:
          break;
      }
    }
  };

  const defaultColumn: Partial<ColumnDef<IIncome>> = {
    cell: function Cell({ getValue, row, column: { id }, table }) {
      const initialValue = getValue();
      const [value, setValue] = React.useState(initialValue);

      const onChange = (e: any) => {
        setValue(e.target.value);
      };

      const onNumberBlur = () => {
        let stringValue = String(value);
        let numberValue: number | null = 0;
        if (!stringValue) {
          stringValue = '';
          numberValue = null;
        } else {
          stringValue = stringValue.replace(',', '.');
          numberValue = parseFloat(stringValue);
        }
        setValue(stringValue);
        props.updateIncomeCalendarSynchronization(row.index, id, numberValue);
      };

      const onComboboxChange = (e: any) => {
        const obj = JSON.parse(e.target.value);
        setValue(obj);
      };

      const onLocationChange = (value: ILocation) => {
        setValue(value);
      };

      const onBlur = () => {
        props.updateIncomeCalendarSynchronization(row.index, id, value);
      };

      React.useEffect(() => {
        setValue(initialValue);
      }, [initialValue]);

      switch (id) {
        case 'itemId':
          return <>{value}</>;
        case 'description':
          return (
            <Form.Control
              size='sm'
              type='text'
              maxLength={128}
              onChange={onChange}
              onBlur={onBlur}
              value={value as string}
            />
          );
        case 'category':
          return (
            <Form.Control
              size='sm'
              as='select'
              value={JSON.stringify(value)}
              onChange={onComboboxChange}
              onBlur={onBlur}
            >
              {categories}
            </Form.Control>
          );
        case 'location':
          return (
            <LocationsAutosuggest
              selectedValue={value as ILocation}
              onChange={onLocationChange}
              locations={table.options.meta?.locations ?? []}
              inputClassName='form-control'
              onBlur={onBlur}
              showForIncome={true}
            />
          );
        case 'quantityType':
          return (
            <Form.Control
              size='sm'
              as='select'
              value={JSON.stringify(value)}
              onChange={onComboboxChange}
              onBlur={onBlur}
            >
              {quantityTypeItems}
            </Form.Control>
          );
        case 'revenue':
        case 'quantity':
          return (
            <Form.Control size='sm' type='text' onChange={onChange} onBlur={onNumberBlur} value={value as string} />
          );
        default:
          return <Form.Control size='sm' type='text' onChange={onChange} onBlur={onBlur} value={value as string} />;
      }
    },
  };

  const table = useReactTable({
    data: props.items,
    columns,
    defaultColumn,
    meta: {
      locations: props.locations,
    },
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <div className='table-responsive'>
        <table
          id={'perform-income-calendar-synchronization-incomes-table'}
          className='table table-bordered table-sm mt-2'
          aria-labelledby='tabelLabel'
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : (
                      <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody ref={tbodyRef}>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} id={row.id} tabIndex={0} onKeyDown={(e) => handleKeyDown(e, row)}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default PerformIncomeCalendarSynchronizationIable;
